import type { ScanResponse } from '../types/card'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://fykokcroushouuyyzurh.supabase.co'
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'sb_publishable_DcH5lddp7bWEuPqrDlOs_w_LCxnc4G6'

/**
 * Send a photo (and optionally a list of card numbers from OCR) to the
 * scan() edge function. Returns ranked candidates.
 *
 * We pre-encode to JPEG via canvas so the server decoder (imagescript,
 * JPEG/PNG only) always gets a supported format — transparently handles
 * HEIC/WebP/etc. from phones.
 */
export async function scan(file: File, cardNumbers: string[] = []): Promise<ScanResponse> {
  const photo = await toJpegBlob(file)
  const form = new FormData()
  form.append('photo', photo, 'photo.jpg')
  if (cardNumbers.length > 0) {
    form.append('card_numbers', cardNumbers.join(','))
  }

  const resp = await fetch(`${SUPABASE_URL}/functions/v1/scan`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    body: form,
  })

  if (!resp.ok) {
    const txt = await resp.text().catch(() => '')
    throw new Error(`scan failed: ${resp.status} ${txt.slice(0, 300)}`)
  }
  return (await resp.json()) as ScanResponse
}

async function toJpegBlob(file: File): Promise<Blob> {
  const img = await loadImage(file)
  const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight))
  const w = Math.round(img.naturalWidth * scale)
  const h = Math.round(img.naturalHeight * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      0.9,
    )
  })
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('image decode failed'))
    }
    img.src = url
  })
}
